import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Award, BookOpen, Check, ChevronLeft, ChevronRight, ClipboardList, Copy, Lock, Users } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { api, TELEGRAM } from "../lib/api";
import { copyOpportunityLink } from "../components/opportunities/OpportunityCard";
import { RegistrationForm } from "../components/opportunities/RegistrationForm";
import { Reveal } from "../components/Reveal";

export default function OpportunityPage() {
  const { id } = useParams();
  const { t, rtl } = useLang();
  const Chevron = rtl ? ChevronLeft : ChevronRight;
  const [opp, setOpp] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setOpp(null); setMissing(false); window.scrollTo(0, 0);
    api.get(`/opportunities/${id}`).then((r) => setOpp(r.data)).catch(() => setMissing(true));
  }, [id]);

  if (missing) return <main className="container"><div className="empty" style={{ margin: "60px 0" }} data-testid="opportunity-not-found">{t.opps.empty}<br /><Link to="/opportunities" className="btn btn-outline btn-sm" style={{ marginTop: 16 }}>{t.nav.opportunities}</Link></div></main>;
  if (!opp) return <main className="page-loader"><span className="loader" /></main>;

  const isFull = opp.status !== "open" || opp.remaining_seats <= 0;
  const facts = [[t.opps.journal, opp.journal_target], [t.opps.lead, opp.lead_researcher], [t.opps.deadline, opp.deadline], [t.opps.type, opp.study_type], [t.opps.stage, opp.stage], [t.opps.price, opp.price_note]].filter(([, v]) => v);

  return (
    <main data-testid="opportunity-page">
      <section className="detail-hero">
        <div className="container">
          <Reveal>
            <div className="breadcrumb"><Link to="/">{t.nav.home}</Link><Chevron size={14} /><Link to="/opportunities">{t.nav.opportunities}</Link><Chevron size={14} /><span style={{ color: "var(--primary)" }}>{opp.specialty_ar}</span></div>
            <div className="detail-badges">
              <span className="badge badge-indigo">{opp.specialty_ar} · {opp.specialty_en}</span>
              {isFull ? <span className="badge badge-muted"><Lock size={12} />{t.opps.full}</span> : <span className="badge badge-green">{t.opps.remain} {opp.remaining_seats} {t.opps.seatsLeft} {opp.total_seats}</span>}
              {opp.group && <span className="badge badge-muted">{opp.group}</span>}
            </div>
            <h1 data-testid="opportunity-title">{opp.title_en}</h1>
            {opp.title_ar && <p className="ar">{opp.title_ar}</p>}
            <div style={{ display: "flex", gap: 10, marginTop: 20, flexWrap: "wrap" }}>
              <button className="btn btn-outline btn-sm" onClick={() => copyOpportunityLink(opp.id, t.opps.copied)} data-testid="detail-copy-link"><Copy size={15} />{t.opps.copyLink}</button>
              {!isFull && <a href="#register" className="btn btn-primary btn-sm" data-testid="detail-register-anchor">{t.opps.register}</a>}
            </div>
          </Reveal>
        </div>
      </section>
      <div className="container detail-layout">
        <div className="detail-main">
          {opp.description && <Reveal className="panel"><h3><BookOpen size={20} />{rtl ? "عن البحث" : "About the study"}</h3><p data-testid="opportunity-description">{opp.description}</p></Reveal>}
          {facts.length > 0 && <Reveal className="panel" delay={0.05}><h3><ClipboardList size={20} />{rtl ? "بيانات الفرصة" : "Facts"}</h3><div className="facts">{facts.map(([k, v]) => <div className="fact" key={k}><small>{k}</small><b>{v}</b></div>)}</div></Reveal>}
          {opp.benefits?.length > 0 && <Reveal className="panel" delay={0.1}><h3><Award size={20} />{rtl ? "مزايا وقيمة المشاركة" : "Benefits"}</h3><ul className="check-list">{opp.benefits.map((b) => <li key={b}><Check size={16} />{b}</li>)}</ul></Reveal>}
          {opp.requirements?.length > 0 && <Reveal className="panel" delay={0.15}><h3><Users size={20} />{rtl ? "متطلبات المشاركة" : "Requirements"}</h3><ul className="check-list">{opp.requirements.map((b) => <li key={b}><Check size={16} />{b}</li>)}</ul></Reveal>}
          {!opp.description && facts.length === 0 && !opp.benefits?.length && !opp.requirements?.length && <div className="panel"><p className="muted">{t.opps.note} 🏆</p></div>}
        </div>
        <aside className="sticky-side" id="register">
          <Reveal delay={0.1}>
            <div className="reg-panel" data-testid="registration-panel">
              {isFull ? <div className="success-box" data-testid="registration-full"><h3>{t.reg.fullTitle}</h3><p className="muted" style={{ marginTop: 8 }}>{t.reg.fullSub}</p><a className="btn btn-outline" style={{ marginTop: 16 }} href={TELEGRAM} target="_blank" rel="noreferrer">Telegram</a></div>
                : <><h3>{t.opps.register}</h3><p className="sub">{t.reg.sub}</p><RegistrationForm opp={opp} /></>}
            </div>
          </Reveal>
        </aside>
      </div>
    </main>
  );
}
